import React from 'react';
import { Link } from 'react-router-dom';
import { User as UserIcon, LogIn, UserPlus, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext.shared';

interface DesktopGuestAccountMenuProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  isDark: boolean;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  menuRef: React.RefObject<HTMLDivElement | null>;
}

export const DesktopGuestAccountMenu: React.FC<DesktopGuestAccountMenuProps> = ({
  isOpen,
  onToggle,
  onClose,
  isDark,
  buttonRef,
  menuRef,
}) => {
  const { t } = useLanguage();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isOpen) {
        onToggle();
      }
      window.requestAnimationFrame(() => {
        menuRef.current?.querySelector<HTMLElement>('a[href]')?.focus();
      });
    }
  };

  return (
    <div className="relative h-full flex items-center">
      <button
        ref={buttonRef}
        id="desktop-guest-account-trigger"
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls="desktop-guest-account-menu"
        aria-label={t('common.guestAccount')}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        className="flex items-center gap-1 xl:gap-1.5 h-full px-2.5 xl:px-3 text-xs font-bold text-content hover:text-ai transition-colors rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-focus whitespace-nowrap group"
      >
        <UserIcon size={14} strokeWidth={2} className="text-content-secondary group-hover:text-ai transition-colors" />
        <span>{t('common.guestAccount')}</span>
        <ChevronDown
          size={11}
          strokeWidth={2.5}
          className={`transition-transform text-content-muted group-hover:text-ai ${isOpen ? 'rotate-180 text-ai' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          id="desktop-guest-account-menu"
          role="menu"
          aria-labelledby="desktop-guest-account-trigger"
          className={`absolute right-0 top-[calc(100%+8px)] mt-0 w-56 rounded-ds-card p-2 z-[100] border animate-fade-in ${
            isDark
              ? 'bg-surface border-border shadow-[0_12px_48px_rgba(0,0,0,0.6)]'
              : 'bg-surface border-border shadow-[0_12px_48px_rgba(15,23,42,0.18)]'
          }`}
        >
          {/* Option 1: Autentificare */}
          <Link
            to="/login"
            role="menuitem"
            onClick={onClose}
            className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-xs font-semibold text-content hover:bg-surface-subtle hover:text-ai transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            <LogIn size={15} strokeWidth={2} className="text-content-muted" />
            <span>{t('common.login')}</span>
          </Link>

          {/* Option 2: Creează cont gratuit - CTA with brand violet accent */}
          <Link
            to="/register"
            role="menuitem"
            onClick={onClose}
            className="flex items-center gap-2.5 w-full mt-1.5 px-3 py-2.5 rounded-lg text-xs font-bold bg-ai text-white hover:bg-ai-hover shadow-xs transition-all active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            <UserPlus size={15} strokeWidth={2.2} />
            <span>{t('common.register')}</span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default DesktopGuestAccountMenu;
